import React, { useState, useCallback, useRef, useEffect, useId } from 'react';
import { cva } from 'class-variance-authority';
import { motion } from 'motion/react';
import { duration } from './primitives/tokens';
import { Portal } from './primitives/Portal';
import { useFloating } from '../../hooks/useFloating';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { zIndex as Z } from '../../tokens/zIndex';
import styles from './Popover.module.css';

// ── CVA ───────────────────────────────────────────────────────────────────

const popover = cva(styles.base, {
  variants: {
    radius: {
      lg: null,
      '2xl': styles.radius2xl,
    },
  },
  defaultVariants: { radius: 'lg' },
});

const popoverBody = cva(styles.bodyBase, {
  variants: {
    padded: {
      true: styles.body,
      false: null,
    },
  },
  defaultVariants: { padded: true },
});

// ── Types ─────────────────────────────────────────────────────────────────

type Placement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end'
  | 'right-start'
  | 'right-end';

type Trigger = 'hover' | 'click' | 'focus';

interface PopoverProps {
  /** Popover body content */
  content: React.ReactNode;
  /** Optional title above content */
  title?: React.ReactNode;
  /** Position relative to trigger */
  placement?: Placement;
  /** How to open the popover */
  trigger?: Trigger | Trigger[];
  /** Controlled open state */
  open?: boolean;
  /** Called when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Show arrow */
  arrow?: boolean;
  /** ARIA role for the popover panel */
  role?: 'dialog' | 'tooltip';
  /** Whether the body applies its default padding. Set false when content is a self-laid-out panel that owns its own spacing. */
  padded?: boolean;
  /** Panel corner radius. Set '2xl' when content is a self-laid-out panel that wants a rounder chrome than the default. */
  radius?: 'lg' | '2xl';
  /** Trigger element */
  children: React.ReactElement;
}

/**
 * Popover — rich content popup attached to a trigger element.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | content | ReactNode | — | Body content |
 * | title | ReactNode | — | Header title |
 * | placement | Placement | 'top' | Position |
 * | trigger | 'hover' \| 'click' \| 'focus' \| array | ['hover', 'focus'] | How to trigger |
 * | open | boolean | — | Controlled state |
 * | onOpenChange | (open) => void | — | State callback |
 * | arrow | boolean | true | Show arrow |
 * | role | 'dialog' \| 'tooltip' | 'dialog' | ARIA role |
 * | padded | boolean | true | Body padding; set false for self-laid-out panel content |
 * | radius | 'lg' \| '2xl' | 'lg' | Panel corner radius; set '2xl' for a rounder self-laid-out panel |
 *
 * @example
 * <Popover content="Hello from popover!" title="Greeting">
 *   <Button>Hover me</Button>
 * </Popover>
 *
 * <Popover content={<UserCard />} trigger="click" placement="bottom-start">
 *   <Avatar />
 * </Popover>
 */
export function Popover({
  content,
  title,
  placement = 'top',
  trigger = ['hover', 'focus'],
  open: controlledOpen,
  onOpenChange,
  arrow = true,
  role = 'dialog',
  padded,
  radius,
  children,
}: PopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const titleId = useId();
  const shouldReduce = useReducedMotion();

  const enterTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const leaveTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const { triggerRef, floatingRef, position } = useFloating<HTMLElement, HTMLDivElement>({
    placement,
    offset: arrow ? 12 : 8,
    open: isOpen,
  });

  const triggers = Array.isArray(trigger) ? trigger : [trigger];

  const show = useCallback(() => {
    clearTimeout(leaveTimer.current);
    enterTimer.current = setTimeout(() => {
      if (!isControlled) setInternalOpen(true);
      onOpenChange?.(true);
    }, 100);
  }, [isControlled, onOpenChange]);

  const hide = useCallback(() => {
    clearTimeout(enterTimer.current);
    leaveTimer.current = setTimeout(() => {
      if (!isControlled) setInternalOpen(false);
      onOpenChange?.(false);
    }, 150);
  }, [isControlled, onOpenChange]);

  const toggle = useCallback(() => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
    const next = !isOpen;
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  }, [isOpen, isControlled, onOpenChange]);

  // Close on outside click. Listens on `pointerdown`, not `mousedown` —
  // pointer events fire BEFORE mousedown and reach `document` even
  // when surfaces like React Flow's pane suppress mousedown
  // propagation. Same fix the core-ui Dropdown applied earlier; the
  // emoji picker in the canvas toolbar wouldn't dismiss on canvas
  // clicks otherwise.
  useEffect(() => {
    if (!isOpen) return;
    const handlePointer = (e: PointerEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      if (floatingRef.current?.contains(e.target as Node)) return;
      // Don't close when a nested floating panel (e.g. ColorPicker dropdown) is clicked
      if ((e.target as Element)?.closest?.('[data-floating-panel]')) return;
      hide();
    };
    document.addEventListener('pointerdown', handlePointer);
    return () => document.removeEventListener('pointerdown', handlePointer);
  }, [isOpen, hide, triggerRef, floatingRef]);

  useEffect(() => {
    return () => {
      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
    };
  }, []);

  const triggerProps: Record<string, unknown> = { ref: triggerRef };
  if (triggers.includes('hover')) {
    triggerProps.onMouseEnter = show;
    triggerProps.onMouseLeave = hide;
  }
  if (triggers.includes('focus')) {
    triggerProps.onFocus = show;
    triggerProps.onBlur = hide;
  }
  if (triggers.includes('click')) {
    triggerProps.onClick = toggle;
  }

  const panelInitial = shouldReduce ? { opacity: 0 } : { opacity: 0, y: 4 };
  const panelAnimate = shouldReduce ? { opacity: 1 } : { opacity: 1, y: 0 };

  return (
    <>
      {React.cloneElement(children, triggerProps)}
      {isOpen && (
        <Portal>
          <motion.div
            ref={floatingRef}
            role={role}
            aria-labelledby={title ? titleId : undefined}
            initial={panelInitial}
            animate={panelAnimate}
            transition={{ duration: duration.normal / 1000 }}
            className={popover({ radius })}
            style={{
              '--popover-top': `${position.top}px`,
              '--popover-left': `${position.left}px`,
              '--popover-z': Z.popover,
            } as React.CSSProperties}
            onMouseEnter={triggers.includes('hover') ? () => clearTimeout(leaveTimer.current) : undefined}
            onMouseLeave={triggers.includes('hover') ? hide : undefined}
          >
            {title && <div id={titleId} className={styles.titleBar}>{title}</div>}
            <div className={popoverBody({ padded })}>{content}</div>
          </motion.div>
        </Portal>
      )}
    </>
  );
}
